import type { AddOnState, AddOnType, MediaProjectResponse } from '../types';


/**
 * The media service is mounted at `${apiBaseUrl}/media`, and the spec paths
 * are relative to it — so `/projects/{id}` → `/media/projects/…` and
 * `/media/assets/{id}` → `/media/media/assets/…` (the doubled segment is
 * intentional; see admin's `useAssetById`).
 */
export const projectUrl = (apiBaseUrl: string, projectId: string): string =>
  `${apiBaseUrl}/media/projects/${projectId}`;

export const assetUrl = (apiBaseUrl: string, assetId: string): string =>
  `${apiBaseUrl}/media/media/assets/${assetId}`;

/**
 * GET a JSON document, rejecting on a non-2xx. The bearer goes only where one
 * is passed: the project and asset reads are authed, the asset's signed path
 * is not — S3 refuses a presigned URL that also carries `Authorization`.
 */
export const readJson = async <T>(
  url: string,
  token?: string | null,
): Promise<T> => {
  const res = await fetch(url, {
    cache: 'no-cache',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error(`${url} → ${res.status}`);
  return (await res.json()) as T;
};

/**
 * Send a JSON body (PATCH by default), rejecting on a non-2xx. Authed like
 * `readJson`; the response body is not read — callers re-read through their
 * own query instead of trusting the write's echo.
 */
export const writeJson = async (
  url: string,
  body: unknown,
  token?: string | null,
  method: 'PATCH' | 'POST' = 'PATCH',
): Promise<void> => {
  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${method} ${url} → ${res.status}`);
};

/**
 * Collect the manifest entry for every add-on present in
 * `storyboards[].add_ons[]` (first storyboard that has one per type) — its
 * `asset_id`, its `secondary_asset_id` (the podcast's mp3) and its status.
 * Only add-ons with an asset count; those are the ones that can be fetched.
 */
export const resolvePresentAddons = (
  project: MediaProjectResponse,
): Map<AddOnType, AddOnState> => {
  const byType = new Map<AddOnType, AddOnState>();
  for (const storyboard of project.storyboards ?? []) {
    for (const addOn of storyboard.add_ons ?? []) {
      if (addOn.asset_id && !byType.has(addOn.add_on_type)) {
        byType.set(addOn.add_on_type, addOn);
      }
    }
  }
  return byType;
};
